import type { Page } from '@alucio/aws-beacon-amplify/src/models';
import type { DocumentVersionORM } from 'src/types/orms';
import type { PayloadGroup } from 'src/components/PresentationBuilder/state/PresentationBuilderStateProvider';
import { GroupStatus } from './groupStatus';
import { getCustomDeckDefaultGroupVisibility } from '../hiddenSlides';

export interface ValidationDependencies {
  createId: () => string;
  getPageWithDocumentVersionHiddenState: <TPage extends Page>(
    page: TPage, documentVersionORM?: DocumentVersionORM,
  ) => TPage;
}

// Preserves editor validation separately from the selector's different expansion order.
export const validatePayloadGroups = (
  existingPayloadGroups: PayloadGroup[],
  dependencies: ValidationDependencies,
  pendingGroupsRevision = false,
): {
  groups: PayloadGroup[],
  numRequiredSlidesAdded: number;
  pendingGroupsRevision: boolean;
} => {
  const { createId, getPageWithDocumentVersionHiddenState } = dependencies;
  const docVersionMap = new Map<string, DocumentVersionORM>();
  const addedPages = new Set<string>();
  const addedPageGroups = new Set<string>();
  let numAddedSlides = 0;

  // We only add required slides if the deck is in a valid state otherwise we let the user keep on editing
  if (!existingPayloadGroups.every((group) => group.groupStatus === GroupStatus.ACTIVE)) {
    // IF THERE ARE ASSOCIATED SLIDES, THE GROUPS NEED TO BE ADDED TO A QUEUE FOR LATER REVIEW
    existingPayloadGroups.forEach((group) => {
      group.pages.forEach((page) => {
        if (page.linkedSlides?.length) {
          pendingGroupsRevision = true;
        }
      });
    });

    return {
      groups: existingPayloadGroups,
      numRequiredSlidesAdded: 0,
      pendingGroupsRevision,
    };
  } else {
    pendingGroupsRevision = false;
  }

  existingPayloadGroups.forEach(group => {
    if (group.documentVersionORM) {
      docVersionMap.set(group.documentVersionORM!.model.id, group.documentVersionORM!);
      group.pages.forEach(page => addedPages.add(page.pageId));
      group.groupSrcId && addedPageGroups.add(group.groupSrcId);
    }
  });

  const newPayloadGroups: PayloadGroup[] = [];

  // We iterate through all documents making sure required group/slides are already added
  // Since now the required slide are added through selection stage, this can be the reassurance
  docVersionMap.forEach((docVer) => {
    const {
      relations: {
        documentORM, pages,
      },
      model:{
        id,
      },
    } = docVer;

    pages.forEach(page => {
      const { model:{ isRequired }, relations:{ pageGroupORM } } = page;
      /** Identify required pages... */
      if (isRequired) {
        /** ... with related page groups that are not currently in the addedPageGroups set and add them to
         * the newPayloadGroups array. Additionally, add the source doc group id to the addedPageGroups set */
        if (pageGroupORM && !addedPageGroups.has(pageGroupORM.model.id)) {
          // const srcDocGroup = pageGroupORM
          /**
           * Map the page models in the group's related PageORM array to a Page array
           * Additionally add each page id to the addedPages set
           * */
          const pages = pageGroupORM.relations.pages.map(({ model }) => {
            addedPages.add(model.pageId);
            return getPageWithDocumentVersionHiddenState(model, docVer);
          });
          newPayloadGroups.push({
            id: createId(),
            groupSrcId: pageGroupORM.model.id,
            documentVersionORM: docVer,
            pages,
            visible: getCustomDeckDefaultGroupVisibility(pages),
            groupStatus: GroupStatus.ACTIVE,
            docAccessLevel: pageGroupORM.relations.documentVersionORM.relations.documentORM.model.accessLevel,
            locked: pageGroupORM.model.locked,
          });
          numAddedSlides = numAddedSlides + pages.length;
          addedPageGroups.add(pageGroupORM.model.id);

        /** ... which are not in the addedPages set and add them to the newPayloadGroups array */
        } else if (!addedPages.has(page.model.pageId)) {
          addedPages.add(page.model.pageId);
          const pageWithHiddenState = getPageWithDocumentVersionHiddenState(page.model, docVer);
          newPayloadGroups.push({
            id: createId(),
            groupSrcId: id,
            documentVersionORM: docVer,
            pages: [pageWithHiddenState],
            visible: getCustomDeckDefaultGroupVisibility([pageWithHiddenState]),
            groupStatus: GroupStatus.ACTIVE,
            docAccessLevel: documentORM.model.accessLevel,
          });
          numAddedSlides++;
        }
      }
    });
  });

  // Now we iterate through all slides and add associated slides
  const associatedPayloadGroups:PayloadGroup[] = [];
  const associatedSlideIds = new Set<string>();
  const allPayloadGroups = [...existingPayloadGroups, ...newPayloadGroups];

  // Iterate over all payload groups and add the associated slides to associatedSlides set
  allPayloadGroups.forEach(({ documentVersionORM, pages, docAccessLevel }) => {
    pages.forEach(page => {
      page.linkedSlides?.forEach(slideId => {
        // If linkedSlide is not already contained in payloadGroups and we have not add it yet
        const existsInPayloadGroups = !existingPayloadGroups.find(group =>
          group.pages.some(page => page.pageId === slideId));
        if (!associatedSlideIds.has(slideId) && existsInPayloadGroups) {
          const associatedSlide = documentVersionORM?.meta.allPages.find(page => page.pageId === slideId);
          if (associatedSlide) {
            // then we will push the association slide in associated array
            associatedSlideIds.add(slideId);
            const pageWithHiddenState = getPageWithDocumentVersionHiddenState(associatedSlide, documentVersionORM);
            const newAssociatedSlide: PayloadGroup = {
              id: createId(),
              groupSrcId: slideId,
              documentVersionORM: documentVersionORM,
              pages: [pageWithHiddenState],
              visible: getCustomDeckDefaultGroupVisibility([pageWithHiddenState]),
              groupStatus: GroupStatus.ACTIVE,
              docAccessLevel: docAccessLevel,
            };
            associatedPayloadGroups.push(newAssociatedSlide);
            numAddedSlides++;
          }
        }
      });
    });
  });

  // Sort all required slides in slide number order
  const sortedByPageNum = [...newPayloadGroups, ...associatedPayloadGroups]
    .sort((a, b) => (a.pages[0].number < b.pages[0].number) ? -1 : 1);
  return {
    groups: [...existingPayloadGroups, ...sortedByPageNum],
    numRequiredSlidesAdded: numAddedSlides,
    pendingGroupsRevision,
  };
};
